import { Box } from '@mui/material';
import { useEffect, useState, useRef } from 'react';

// Define color stops: Purple, Cyan, Blue, Pink
const colors = [
  { r: 139, g: 92, b: 246 }, // Purple (#8b5cf6)
  { r: 6, g: 182, b: 212 }, // Cyan (#06b6d4)
  { r: 59, g: 130, b: 246 }, // Blue (#3b82f6)
  { r: 236, g: 72, b: 153 }, // Pink (#ec4899)
] as const;

const DEFAULT_COLOR = { r: 139, g: 92, b: 246 }; // Purple fallback

export const SpotlightEffect = () => {
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [isVisible, setIsVisible] = useState(false);
  const [colorIndex, setColorIndex] = useState(0);
  const lastPositionRef = useRef({ x: 0, y: 0 });
  const lastTimeRef = useRef(0);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      const currentTime = Date.now();
      const timeDelta = currentTime - lastTimeRef.current;

      // Calculate distance moved
      const dx = e.clientX - lastPositionRef.current.x;
      const dy = e.clientY - lastPositionRef.current.y;
      const distance = Math.sqrt(dx * dx + dy * dy);

      // Calculate velocity (pixels per millisecond)
      const velocity = timeDelta > 0 ? distance / timeDelta : 0;

      // Update color index based on velocity
      // Higher velocity = faster color change
      // Scale factor: adjust this to control sensitivity (0.5 works well)
      const colorIncrement = velocity * 0.05;

      setColorIndex((prev) => (prev + colorIncrement) % colors.length);
      setMousePosition({ x: e.clientX, y: e.clientY });
      setIsVisible(true);

      // Update refs for next calculation
      lastPositionRef.current = { x: e.clientX, y: e.clientY };
      lastTimeRef.current = currentTime;
    };

    const handleMouseLeave = () => {
      setIsVisible(false);
    };

    window.addEventListener('mousemove', handleMouseMove);
    document.body.addEventListener('mouseleave', handleMouseLeave);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      document.body.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, []);

  // Calculate current color based on color index
  const getCurrentColor = () => {
    const colorIndexFloor = Math.floor(colorIndex);
    const colorIndexCeil = (colorIndexFloor + 1) % colors.length;
    const colorBlend = colorIndex - colorIndexFloor;

    const color1 = colors[colorIndexFloor] ?? DEFAULT_COLOR;
    const color2 = colors[colorIndexCeil] ?? DEFAULT_COLOR;

    const r = Math.round(color1.r + (color2.r - color1.r) * colorBlend);
    const g = Math.round(color1.g + (color2.g - color1.g) * colorBlend);
    const b = Math.round(color1.b + (color2.b - color1.b) * colorBlend);

    const baseOpacity = 0.15;

    return {
      primary: `rgba(${r}, ${g}, ${b}, ${baseOpacity})`,
      secondary: `rgba(${r}, ${g}, ${b}, ${baseOpacity * 0.7})`,
      tertiary: `rgba(${r}, ${g}, ${b}, ${baseOpacity * 0.5})`,
    };
  };

  const spotlightColor = getCurrentColor();

  return (
    <Box
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'none',
        zIndex: 9999,
        opacity: isVisible ? 1 : 0,
        transition: 'opacity 0.3s ease',
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          width: '600px',
          height: '600px',
          borderRadius: '50%',
          background: `radial-gradient(circle,
            ${spotlightColor.primary} 0%,
            ${spotlightColor.secondary} 25%,
            ${spotlightColor.tertiary} 50%,
            transparent 70%
          )`,
          transform: 'translate(-50%, -50%)',
          left: `${mousePosition.x}px`,
          top: `${mousePosition.y}px`,
          transition: 'left 0.1s ease, top 0.1s ease',
          filter: 'blur(40px)',
        }}
      />
    </Box>
  );
};
